// Audit Types

import { ReactNode } from 'react';
import { Agent, Traveler } from '@marageti/z4-sdk/lib/people';
import {
  Partner,
  TripRequest,
  TripMatch,
  Review,
} from '@marageti/z4-sdk/lib/travel';
import { User } from '@marageti/z4-sdk/lib/auth';

// Re-export SDK types used across audit views
export type {
  ApiEvent,
  SalesStage,
  SalesStageAction,
  SalesStageAudit,
} from '@marageti/z4-sdk/lib/tracking';

// Entity Version types
export type EntityType =
  | 'trip_requests'
  | 'trip_matches'
  | 'partners'
  | 'agents'
  | 'travelers'
  | 'users'
  | 'reviews';

// Union type for all entity versions - reuses existing SDK types
export type AnyEntityVersion =
  | Agent
  | Traveler
  | User
  | Partner
  | TripRequest
  | TripMatch
  | Review;

// Simplified EntityVersion for table display (common fields across all entities)
export interface EntityVersion {
  id: string;
  updateId: string;
  updatedAt: string;
  status?: string;
}

// Helper to extract status from any entity version
export const getEntityVersionStatus = (
  entity: AnyEntityVersion,
): string | undefined => {
  // For entities with direct status field (User, Partner, TripRequest, TripMatch, Review)
  if ('status' in entity && typeof entity.status === 'string') {
    return entity.status;
  }
  // For Agent/Traveler, status is in userInfo[0].status
  if ('userInfo' in entity && Array.isArray(entity.userInfo) && entity.userInfo.length > 0) {
    return entity.userInfo[0]?.status;
  }
  return undefined;
};

// Helper to convert AnyEntityVersion to simplified EntityVersion for table display
export const toEntityVersion = (entity: AnyEntityVersion): EntityVersion => ({
  id: entity.id,
  updateId: entity.updateId,
  updatedAt: entity.updatedAt,
  status: getEntityVersionStatus(entity),
});

// Audit Category for main page
export interface AuditCategory {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: ReactNode;
}

// Filter types
export interface EventsFilters {
  day?: string;
  entity?: string;
  limit?: number;
  logLevel?: string;
  reverse?: boolean;
}

export interface VersionsFilters {
  entityType?: EntityType;
  id?: string;
  all?: boolean;
}

export interface SalesStageFilters {
  tripMatchId?: string;
  partnerId?: string;
  assignedAgentId?: string;
  stage?: string;
  /** String 'true' or 'false' for backend filter compatibility */
  overdue?: string;
  actionTaken?: string;
  startDate?: string;
  dueAt?: string;
}

export interface ViewsFilters {
  entity?: string;
  day?: string;
  device?: string;
}
